/**
 * Spec Phase 11 Slice 11E — keyboard shortcuts.
 *
 * Wires the brief §6 shortcut table:
 *
 *   Cmd/Ctrl + Enter       → submit AI prompt bar (NL edit)
 *   Cmd/Ctrl + G           → generate active section
 *   Cmd/Ctrl + Shift + G   → generate everything
 *   Cmd/Ctrl + →           → next section
 *   Cmd/Ctrl + ←           → previous section
 *   Cmd/Ctrl + S           → force save (flush offline queue)
 *   Cmd/Ctrl + B           → toggle left rail
 *   Cmd/Ctrl + Shift + B   → toggle right rail
 *   Cmd/Ctrl + K           → focus AI prompt bar
 *   ?                      → open shortcut cheatsheet
 *   Esc                    → close any open modal / cancel
 *                            running generate-everything
 *
 * Active only when the editor surface is focused (or no input
 * has focus). Drafter typing into the right-rail's claim card
 * edit shouldn't trigger Cmd+G — we detect the focused element
 * and skip the shortcut path when the focus is inside the
 * right-rail or an admin form.
 */
import { useEffect } from 'react';
import { useSpecStore } from '@/store/specStore';


function _isModKey(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey;
}


function _focusInsideSpecEditor(): boolean {
  const el = document.activeElement;
  if (!el || el === document.body) return true;
  // Focused inside the spec paper editor — yes, run shortcuts.
  if (el.closest('[data-testid="spec-paper-editor"]')) return true;
  // Focused inside the prompt bar — Enter / K should still apply.
  if (el.closest('[data-testid="spec-prompt-bar"]')) return true;
  // Anything else (right-rail dock inputs, top-level modals,
  // admin forms) — skip the shortcut path so drafters typing
  // into a claim card can use Cmd+S etc. as native browser
  // behaviour.
  return false;
}


export function useSpecKeyboardShortcuts(args: {
  /** Open the cheatsheet modal — toggled by '?'. */
  onOpenCheatsheet: () => void;
}): void {
  const { onOpenCheatsheet } = args;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const store = useSpecStore.getState();
      const mod = _isModKey(e);

      // ── ? — cheatsheet (no modifier; works anywhere) ─────────
      if (e.key === '?' && !mod && !e.altKey) {
        // Skip when focus is in a textarea/input (so '?' types
        // normally inside a paragraph or form field).
        const el = document.activeElement as HTMLElement | null;
        if (el && (
          el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
          || el.isContentEditable
        )) {
          return;
        }
        e.preventDefault();
        onOpenCheatsheet();
        return;
      }

      // ── Esc — defer to native modal handlers ────────────────
      // Each modal already wires its own Esc; we don't intercept
      // here so we don't double-fire. (Documented in the
      // cheatsheet for completeness.)
      if (e.key === 'Escape') return;

      // Everything below requires Cmd/Ctrl.
      if (!mod) return;

      // Skip when focus is outside the spec editor surface.
      if (!_focusInsideSpecEditor()) return;

      const matterId = store.matterId;
      const activeSectionId = store.activeSectionId;
      const sections = store.sections;

      // ── Cmd+Enter — submit prompt bar ────────────────────────
      if (e.key === 'Enter') {
        const promptBar = document.querySelector<HTMLButtonElement>(
          '[data-testid="spec-prompt-send"]',
        );
        if (promptBar && !promptBar.disabled) {
          e.preventDefault();
          promptBar.click();
        }
        return;
      }

      // ── Cmd+G / Cmd+Shift+G ──────────────────────────────────
      if (e.key === 'g' || e.key === 'G') {
        if (e.shiftKey) {
          // Cmd+Shift+G → Generate everything (header button).
          const btn = document.querySelector<HTMLButtonElement>(
            '[data-testid="spec-generate-everything"]',
          );
          if (btn && !btn.disabled) {
            e.preventDefault();
            btn.click();
          }
          return;
        }
        // Cmd+G → generate active section.
        if (!activeSectionId || !matterId) return;
        const section = sections.find((s) => s.id === activeSectionId);
        if (!section || section.is_auto_generated || section.is_frozen) return;
        e.preventDefault();
        // Confirmation when section already has content; the
        // store's generateSection action surfaces the
        // pendingOverwrite branch and the OverwriteConfirmDialog
        // renders automatically.
        void store.generateSection({
          sectionId: activeSectionId,
          drafterIntent: '',
          overwrite: false,
        });
        return;
      }

      // ── Cmd+→ / Cmd+← — next / previous section ─────────────
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        if (!activeSectionId) return;
        const idx = sections.findIndex((s) => s.id === activeSectionId);
        if (idx < 0) return;
        const nextIdx = e.key === 'ArrowRight'
          ? Math.min(sections.length - 1, idx + 1)
          : Math.max(0, idx - 1);
        if (nextIdx === idx) return;
        e.preventDefault();
        store.setActiveSection(sections[nextIdx].id);
        return;
      }

      // ── Cmd+S — force save (flush offline queue) ────────────
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        void store.flushOfflineQueue();
        store.showToast('Saved.');
        return;
      }

      // ── Cmd+B / Cmd+Shift+B — rail toggles ──────────────────
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        if (e.shiftKey) store.toggleRightRail();
        else store.toggleLeftRail();
        return;
      }

      // ── Cmd+K — focus prompt bar ────────────────────────────
      if (e.key === 'k' || e.key === 'K') {
        const input = document.querySelector<HTMLTextAreaElement>(
          '[data-testid="spec-prompt-input"]',
        );
        if (input) {
          e.preventDefault();
          input.focus();
        }
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenCheatsheet]);
}
